import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Send, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';

/**
 * Reusable dialog to send a purchase request email to the purchaser(s).
 * Props:
 *  - open: boolean
 *  - onOpenChange: fn(bool)
 *  - requestType: 'low_stock' | 'vendor_order' | 'general'
 *  - item: optional { id, item_name, item_sku, current_quantity, base_uom, reorder_point }
 *  - vendor: optional { id, vendor_name }
 *  - defaultSubject: optional string
 *  - defaultBody: optional string
 */
const EmailPurchaserDialog = ({
  open,
  onOpenChange,
  requestType = 'general',
  item = null,
  vendor = null,
  defaultSubject = '',
  defaultBody = '',
}) => {
  const { api } = useAuth();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  useEffect(() => {
    if (!open) return;
    api().get('/email-templates/predefined')
      .then(r => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [open]);

  const applyTemplate = async (templateId) => {
    if (!templateId) return;
    setApplyingTemplate(true);
    try {
      const r = await api().post('/email-templates/render', {
        template_id: templateId,
        item_id: item?.id || null,
        vendor_id: vendor?.id || null,
      });
      setSubject(r.data.subject || '');
      setBody(r.data.body || '');
      toast.success('Template applied');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to render template');
    } finally {
      setApplyingTemplate(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    // Build smart defaults
    let s = defaultSubject;
    let b = defaultBody;

    if (!s) {
      if (requestType === 'low_stock' && item) {
        s = `Low stock alert: ${item.item_name} (${item.item_sku})`;
      } else if (requestType === 'vendor_order' && vendor) {
        s = `Purchase request — ${vendor.vendor_name}`;
      } else {
        s = 'Purchase request';
      }
    }

    if (!b) {
      if (requestType === 'low_stock' && item) {
        b = `Hi,\n\nWe're running low on "${item.item_name}" (SKU: ${item.item_sku}). Current on-hand is ${item.current_quantity} ${item.base_uom || ''}, which is below the reorder point of ${item.reorder_point || 0}.\n\nPlease initiate a reorder when convenient.\n\nThanks.`;
      } else if (requestType === 'vendor_order' && vendor) {
        b = `Hi,\n\nPlease prepare a purchase order for ${vendor.vendor_name}.\n\nDetails to include:\n- Items / quantities:\n- Required by:\n- Notes:\n\nThanks.`;
      } else {
        b = 'Hi,\n\nPlease review the attached purchasing request.\n\nThanks.';
      }
    }
    setSubject(s);
    setBody(b);
    setCcEmail('');
  }, [open, requestType, item, vendor, defaultSubject, defaultBody]);

  const handleSubmit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        request_type: requestType,
        subject: subject.trim(),
        body: body.trim(),
        item_id: item?.id || null,
        vendor_id: vendor?.id || null,
        cc_email: ccEmail.trim() || null,
      };
      const res = await api().post('/notifications/email-purchaser', payload);
      const data = res.data;
      if (data.status === 'sent') {
        toast.success(`Email sent to ${data.sent_to.join(', ')}`);
      } else if (data.status === 'partial') {
        toast.warning(`Email partially sent. Failed for: ${(data.failed || []).join(', ')}`);
      } else {
        toast.success(`Request queued. ${data.in_app_notified} purchaser(s) notified in-app.${data.smtp_configured ? '' : ' (SMTP not configured — configure in Admin Settings to enable email delivery.)'}`);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send purchase request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="email-purchaser-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            Email Purchaser
          </DialogTitle>
          <DialogDescription>
            Send a purchase request to your purchasing team.
            {item && <span className="block mt-1 text-slate-700">Item: <span className="font-medium">{item.item_name}</span> (SKU: {item.item_sku})</span>}
            {vendor && <span className="block mt-1 text-slate-700">Vendor: <span className="font-medium">{vendor.vendor_name}</span></span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {templates.length > 0 && (
            <div data-testid="purchaser-templates">
              <Label className="flex items-center gap-1 text-xs text-slate-500 uppercase tracking-wider">
                <Sparkles className="w-3 h-3" /> Predefined Template
              </Label>
              <select
                onChange={(e) => applyTemplate(e.target.value)}
                disabled={applyingTemplate}
                className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm mt-1"
                data-testid="purchaser-template-select"
                defaultValue=""
              >
                <option value="">— Apply template —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label htmlFor="purchaser-subject">Subject</Label>
            <Input
              id="purchaser-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="purchaser-subject-input"
            />
          </div>
          <div>
            <Label htmlFor="purchaser-body">Message</Label>
            <RichTextEditor
              testId="purchaser-body-input"
              value={body}
              onChange={(html) => setBody(html)}
              variables={['item_sku','item_name','current_quantity','reorder_point','vendor_name','requested_by']}
              placeholder="Compose your message…"
              minHeight={200}
            />
          </div>
          <div>
            <Label htmlFor="purchaser-cc">CC Email <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              id="purchaser-cc"
              type="email"
              placeholder="someone@company.com"
              value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
              data-testid="purchaser-cc-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="purchaser-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="purchaser-send-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPurchaserDialog;
